import React, { Component } from "react";
import axios from "axios";
import Header from "./Components/Header";
import Footer from "./Components/Footer";
import Chart from "./Components/Chart";
import DatePickerComp from "./Components/DatePickerComp";
import IndustryElementSelector from "./Components/IndustryMultiSelect";
import moment from "moment";
import loadScripts from "./loadScripts";
import EmailForm from './Components/EmailForm';
import DayFlux from "./Components/DayFlux";
import Gauge from "./Components/Gauge";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsDownToLine } from '@fortawesome/free-solid-svg-icons';

import "./lib/scss/style.scss";

class App extends Component {
	state = {
		weekData: [],
		chartData: [],
		industries: [],
		endDate: moment(),
		from: moment().subtract(6, "days"),
		to: moment(),
		tweetsDate: null,
		tweetsPage: 0,
		tweets: [],
		tweetHasNext: false,
		proxyUrlSrc: '/wp-content/plugins/wp-rankflux/proxy.php',
		loadingText: "Loading RankFlux data...",
		loading: true,
		haveData: null,
		baseApiPath: process.env.REACT_APP_BACKEND,
		isAvg: false,
		chartVisible: false,
	};

	componentDidMount = () => {
		loadScripts();
		this.loadData();
	};

	loadIndustries = async () => {
		const { baseApiPath } = this.state;
		const url = `${baseApiPath}/api/industries`;

		return axios
			.get(url)
			.then(({ data }) => {
				// Step 1: Group industries by their name
				const grouped = data.reduce((acc, industry) => {
					if (!acc[industry.name]) {
						acc[industry.name] = [];
					}
					acc[industry.name].push(industry);
					return acc;
				}, {});

				// Step 2: Process groups to keep the second occurrence of duplicates and unique entries
				const filteredData = Object.values(grouped).flatMap(group => {
					if (group.length > 1) {
						// For duplicates, keep all but the first
						return group.slice(1);
					}
					// For unique entries, keep them as-is
					return group;
				});

				// Log filtered data for debugging
				 // console.log('[App][LoadIndustries] Filtered industries data:', filteredData);

				return filteredData;
			})
			.catch(error => {
				this.setState({ loadingText: "Error: Industry data failed to load" });
				console.error('[App][LoadIndustries] Error loading industries:', error);
				throw error;
			});
	};



	loadData = async () => {
		try {
			this.setState({ loadingText: "Loading RankFlux gauge..." }); // Update loading text
			const weekDataPromise = this.loadWeekData();
			const industriesPromise = this.loadIndustries();

			const [weekData, industries] = await Promise.all([weekDataPromise, industriesPromise]);

			this.setState({ weekData, industries });

			await this.loadChart();
		} catch (error) {
			this.setState({ loadingText: "Error: Data not loaded" });
			console.error('[App][loadData] Error loading data:', error);
		}
	};

	loadWeekData = async () => {
		const { baseApiPath } = this.state;
		const fromVar = moment().subtract(6, "days");
		const toVar = moment();

		const params = new URLSearchParams({
			from: fromVar.format('YYYY-MM-DD'),
			to: toVar.format('YYYY-MM-DD'),
			avg: 1,
			chart: 1,
		});

		const url = `${baseApiPath}/api/data?${params.toString()}`;

		try {
			const { data } = await axios.get(url);
			// console.log('[App][loadWeekData] Data received:', data);

			if (data.length > 0) {
				const output = data.reverse().map(item => ({
					date: item.date,
					avg: item.points.avg,
					status: item.status || 'completed', // Handle status
				}));

				output.forEach(item => {
					// console.log(`[App][loadWeekData] Date: ${item.date}, Status: ${item.status}`);
					if (item.status === 'calculating') {
						// console.log(`[App][loadWeekData] Processing data for date ${item.date}`);
						item.avg = 'Processing'; // Use "Processing" while calculating
					} else if (item.status === 'no-data') {
						// console.log(`[App][loadWeekData] No data available for date ${item.date}`);
						item.avg = 'No result'; // Use "No result" when no data
					} else if (item.status === 'completed' && item.avg === 0) {
						item.avg = 'No result';
					}
				});

				// console.log('Status:', output.map(item => item.status));

				const endDate = moment(output[0].date, 'YYYY-MM-DD');
				const yesterdayFlux = output.length > 1 ? output[1].avg : null;

				this.setState({
					weekData: output,
					endDate,
					from: endDate.clone().subtract(6, 'days'),
					to: endDate,
					haveData: !!output.find(item => item.status === 'completed'),
					yesterdayFlux
				});

				return output;
			}
		} catch (error) {
			console.error('[App][loadWeekData] Error:', error);
			this.setState({ loadingText: "Error: Week data failed to load" });
			throw error;
		}
	};


	toggleChartVisibility = () => {
		this.setState(prevState => ({ chartVisible: !prevState.chartVisible }));
	};

	loadChart = async () => {
		const { industries, from, to, baseApiPath } = this.state;

		const industriesStr = industries
			.filter(e => e.selected)
			.map(e => e.id)
			.join(',');

		const params = new URLSearchParams({
			from: from.format('YYYY-MM-DD'),
			to: to.format('YYYY-MM-DD'),
			industries: industriesStr,
			avg: 1,
			chart: 1,
		});

		const url = `${baseApiPath}/api/data?${params.toString()}`;
		// console.log('Constructed URL for chart data:', url);

		try {
			this.setState({ loadingText: "Loading RankFlux chart..." });

			const { data } = await axios.get(url);

			// const sum = data.reduce((acc, item) => acc + parseFloat(item.points.avg), 0);
			// const count = data.length;
			// const replacementAvg = (sum / count).toFixed(2);

			data.forEach(item => {
				// console.log('[APP] from server avg:', item.points.avg)
				if (isNaN(item.points.avg) || item.points.avg >= 7 || item.points.avg === 0) {

					// console.log('[APP] Average added');
					item.points.avg = 0;//+replacementAvg;
					this.setState.isAvg = true;
				}
			});

			this.setState({
				chartData: data,
				loading: false
			});
		} catch (error) {
			this.setState({ loadingText: "Error: Chart data failed to load" });
			console.error('Error loading chart data:', error);
			throw error;
		}
	};

	changeIndustries = industries => {
		this.setState({ industries }, this.loadChart);
	};

	changeDate = ({ from, to }) => {
		this.setState({ from, to }, this.loadChart);
	};

	render() {
		const {
			weekData,
			haveData,
			chartData,
			industries,
			endDate,
			from,
			to,
			loadingText,
			loading,
			chartVisible,
		} = this.state;

		const manualColours = [ // For the legend in the chart
			'#8baf35', '#acd844', '#eadb49', '#f3a83c', '#ed662c',
			'#ec5729', '#ec4a27', '#eb3f25', '#eb3724', '#eb3223'
		];

		// console.log('[App][render] State:', this.state);

		if (loading) {
			return (
				<div className="rf-loader">
					<div className="rf-spinner"></div>
					<div className="rf-loading-text">{loadingText}</div>
				</div>
			);
		}

		return (
			<div className={`app${loading ? " loading" : ""}`}>
				<Header weekData={weekData} />

				{!haveData && (
					<div className="bl-rankflux">
						<div className="bl-widget">
							<p><strong>Oops! Something's not right.</strong></p>
							<p>We know there's an issue and we're working on it. Please try again later. Thanks!</p>
						</div>
					</div>
				)}

				
				<div className="bl-toggle-chart">
					<button
						className="toggle-chart-btn"
						onClick={this.toggleChartVisibility}
					>
						{chartVisible ? "Hide weekly chart" : "Show weekly chart"}
					</button>
				</div>

				<div className="bl-rankflux">
					
					<div className="bl-widget" style={{ opacity: !haveData ? 0.3 : 1 }}>
						
						<div className="bl-widget-gauge">
							<Gauge
								day={weekData[0] && weekData[0].date}
								flux={weekData[0] && weekData[0].avg}
								manualColours={manualColours}
								yesterdayFlux={this.state.yesterdayFlux}
							/>
						</div>
						
					</div>

					<div className="bl-widget-wide">
						{haveData && (
							<div className="bl-widget-days-container" style={{ opacity: !haveData ? 0.3 : 1 }}>
								<div className="bl-widget-title">
									<h6>Last 7 days, average for all industries:</h6>
								</div>
								<div className="bl-widget-days">
									
									{weekData && weekData.map((e, i) => {
										const date = moment(e.date, "YYYY-MM-DD");
										const avg = e.avg;

										return (
											<DayFlux
												key={i}
												isLast={i === 0}
												day={date}
												flux={avg} // Pass "Processing" or "No result" directly as flux
												manualColours={manualColours}
											/>
										);
									}).reverse()}
								</div>
								<div className="bl-widget-callout">
									<a href="#about-rankflux">
										<FontAwesomeIcon icon={faArrowsDownToLine} />&nbsp;&nbsp;Learn how we calculate the Local RankFlux Score
									</a>
								</div>
							</div>
						)}
					</div>

					<div className={`bl-chart-slide-container ${chartVisible ? "visible" : ""}`}>
						{/* <div className="bl-header-chart">
							<h3>Weekly Chart</h3>
						</div> */}

						<div className="bl-chart-wrapper" id="weekly-chart">
							<div className="bl-filter">
								<IndustryElementSelector
									industries={industries}
									onChange={this.changeIndustries}
								/>

								<DatePickerComp
									from={from}
									to={to}
									onChange={this.changeDate}
									endDate={endDate}
								/>
							</div>

							<div className="bl-chart-inner">
								<Chart
									industries={industries}
									data={chartData}
									onClick={this.changeTweetsDate}
								/>
							</div>
						</div>
					</div>
				</div>

				<EmailForm />

				<Footer weekData={weekData} />
			</div>
		);
	}

}

export default App;
